/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { Spin, Table, Tooltip, notification } from 'antd';
import { Button as CrceButton, DatePicker as CrceDatePicker } from '@crc-evans/ui-components';
import { ChevronDown, ChevronRight, FileText, MessageSquare } from 'lucide-react';
import { fetchDsrLogs } from '../redux/dsr/action';
import { connect } from 'react-redux';
import { get } from 'lodash';
import moment from 'moment';
import axiosConfig from '../services/axiosConfig';
import { fetchToolBoxHome } from '../redux/toolbox/action';
import './ToolboxHome.css';
import './ShippingManagement/ShipmentDashBoardPage.css';
import { getProjectTableLoadingConfig } from './ProjectTableLoader';
import { ProjectTabTableEmpty } from './project/ProjectTabTableEmpty';

const formatCommunicationsApiDate = (m) => m.format('YYYY-MM-DD HH:mm:ss');

/** Default project Communications list: last 12 months through today. */
const getLastYearCommunicationsRange = () => {
  const endM = moment().endOf('day');
  const startM = moment().subtract(1, 'year').startOf('day');
  return {
    fromStr: startM.format('YYYY-MM-DD'),
    toStr: endM.format('YYYY-MM-DD'),
    startDate: formatCommunicationsApiDate(startM),
    endDate: formatCommunicationsApiDate(endM),
  };
};

const collectAttendeeNames = (detail) => {
  const names = [];
  const seen = new Set();
  const add = (name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    names.push(trimmed);
  };

  add(detail?.presenterName);
  (detail?.communicationRegisterAttendee || []).forEach((attendee) => add(attendee?.name));
  return names;
};

const CommunicationExpand = ({ communicationRegisterId }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setFailed(false);
      try {
        const res = await axiosConfig.get(
          `communication-register?communicationRegisterId=${communicationRegisterId}`
        );
        if (!cancelled) setDetail(get(res, ['data', 'data'], null));
      } catch (error) {
        if (!cancelled) {
          setDetail(null);
          setFailed(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (communicationRegisterId) load();
    else setLoading(false);

    return () => {
      cancelled = true;
    };
  }, [communicationRegisterId]);

  if (loading) {
    return (
      <div className="communications-expand communications-expand--loading">
        <Spin size="small" />
      </div>
    );
  }

  if (failed) {
    return (
      <div className="communications-expand">
        <div className="communications-expand-box">Could not load this communication.</div>
      </div>
    );
  }

  const attendees = collectAttendeeNames(detail);
  const notes = String(detail?.meetingNotes || '').trim();
  const comments = String(detail?.comments || '').trim();

  return (
    <div className="communications-expand">
      <div className="communications-expand-section">
        <div className="communications-expand-label">Attendees</div>
        <div className="communications-expand-box">{attendees.length ? attendees.join(', ') : '—'}</div>
      </div>
      <div className="communications-expand-section">
        <div className="communications-expand-label">Meeting notes</div>
        <div className="communications-expand-box">{notes || '—'}</div>
      </div>
      {comments ? (
        <div className="communications-expand-section">
          <div className="communications-expand-label">Comments</div>
          <div className="communications-expand-box">{comments}</div>
        </div>
      ) : null}
    </div>
  );
};

const ToolboxHome = (props) => {
  const { selectedProject, onFetchDsrLogs, onFetchToolBox, toolBoxData, isToolboxLoading } = props;
  const dsrLog = [...(Array.isArray(toolBoxData) ? toolBoxData : [])].sort(
    (a, b) => new Date(b.meetingTime) - new Date(a.meetingTime)
  );

  const [communicationsDateFrom, setCommunicationsDateFrom] = useState('');
  const [communicationsDateTo, setCommunicationsDateTo] = useState('');
  const [communicationsPage, setCommunicationsPage] = useState(1);
  const [communicationsPageSize, setCommunicationsPageSize] = useState(20);

  const applyCommunicationsDateRangeToFetch = (fromStr, toStr) => {
    if (!selectedProject?.id || !fromStr || !toStr) return;
    const startM = moment.min(moment(fromStr), moment(toStr)).startOf('day');
    const endM = moment.max(moment(fromStr), moment(toStr)).endOf('day');
    onFetchToolBox({
      selectedProject,
      startDate: formatCommunicationsApiDate(startM),
      endDate: formatCommunicationsApiDate(endM),
    });
  };

  const resetCommunicationsFilterToDefaultRange = () => {
    const { fromStr, toStr } = getLastYearCommunicationsRange();
    setCommunicationsDateFrom(fromStr);
    setCommunicationsDateTo(toStr);
    setCommunicationsPage(1);
    applyCommunicationsDateRangeToFetch(fromStr, toStr);
  };

  useEffect(() => {
    if (selectedProject?.id) {
      const { fromStr, toStr } = getLastYearCommunicationsRange();
      setCommunicationsDateFrom(fromStr);
      setCommunicationsDateTo(toStr);
      setCommunicationsPage(1);
      applyCommunicationsDateRangeToFetch(fromStr, toStr);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when project changes only
  }, [selectedProject]);

  const communicationTypes = [
    { code: 'SA', label: 'Safety Alert' },
    { code: 'TB', label: 'Toolbox Talk' },
    { code: 'EN', label: 'Engineering Notification' },
    { code: 'QA', label: 'Quality Alert' },
    { code: 'EA', label: 'Environmental Alert' },
    { code: 'RA', label: 'Risk Assessments' },
  ];

  const columns = [
    {
      title: 'Date',
      dataIndex: 'meetingTime',
      key: 'date',
      render: (text) => (text ? moment.utc(text).format('DD MMM YYYY') : '-'),
      align: 'center',
    },
    {
      title: 'Title',
      key: 'title',
      align: 'left',
      ellipsis: {
        showTitle: false,
      },
      render: (data, record) => truncateTableData(record.title),
    },
    {
      title: 'Type',
      dataIndex: 'communicationType',
      key: 'communicationType',
      align: 'left',
      ellipsis: {
        showTitle: false,
      },
      render: (data) => {
        const foundType = communicationTypes.find((type) => type.code === data);
        return foundType ? foundType.label : data;
      },
    },
    {
      title: 'Presented By',
      key: 'presenterName',
      render: (text, record) => {
        return <div>{record.presenterName}</div>;
      },
      align: 'center',
    },
    {
      title: 'Report',
      dataIndex: 'reportDetails',
      key: 'reportDetails',
      align: 'center',
      render: (text, record) => {
        return (
          <>
            {record &&
            record.reportDetails !== null &&
            JSON.stringify(record.reportDetails) != '{}' ? (
              <Tooltip title="View / Download Report">
                <a
                  href={record?.reportDetails?.fileUrl}
                  className="communications-doc-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="View or download report"
                >
                  <FileText size={18} strokeWidth={1.75} />
                </a>
              </Tooltip>
            ) : (
              <Tooltip title="Report Not Generated">
                <span className="communications-doc-link is-disabled" aria-hidden>
                  <FileText size={18} strokeWidth={1.75} />
                </span>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  const truncateTableData = (data) => {
    return (
      <Tooltip placement="topLeft" title={data}>
        {data}
      </Tooltip>
    );
  };

  const runFetchForDateRangeStrings = (fromStr, toStr) => {
    if (!fromStr || !toStr) return;
    const startM = moment.min(moment(fromStr), moment(toStr)).startOf('day');
    const endM = moment.max(moment(fromStr), moment(toStr)).endOf('day');
    const dateObj = {
      fromDate: formatCommunicationsApiDate(startM),
      toDate: formatCommunicationsApiDate(endM),
    };
    if (selectedProject?.id) {
      setCommunicationsPage(1);
      onFetchDsrLogs({ selectedProject, dateObj });
      onFetchToolBox({
        selectedProject,
        startDate: dateObj.fromDate,
        endDate: dateObj.toDate,
      });
    } else {
      notification.warning({
        message: 'Please select Project to create Pipeline',
      });
    }
  };

  const handleCommunicationsDatePartChange = (which, value) => {
    const nextFrom = which === 'from' ? value || '' : communicationsDateFrom;
    const nextTo = which === 'to' ? value || '' : communicationsDateTo;
    setCommunicationsDateFrom(nextFrom);
    setCommunicationsDateTo(nextTo);

    if (!nextFrom && !nextTo) {
      resetCommunicationsFilterToDefaultRange();
      return;
    }
    if (nextFrom && nextTo) {
      runFetchForDateRangeStrings(nextFrom, nextTo);
    }
  };

  const handleCommunicationsDateRangeReset = () => {
    resetCommunicationsFilterToDefaultRange();
  };

  return (
    <div className="modern-communications-page">
      <div className="communications-header">
        <div className="communications-header-text">
          <div className="communications-header-icon" aria-hidden>
            <MessageSquare size={22} color="#00a88e" />
          </div>
          <div className="communications-header-titles">
            <h1 className="communications-title">Communications</h1>
            <p className="communications-subtitle">List of communications for this project.</p>
          </div>
        </div>
        <div className="communications-toolbar">
          <div className="communications-date-range" role="group" aria-label="Filter by date range">
            <div className="communications-crce-field">
              <span className="communications-crce-field-label" id="communications-from-label">
                From
              </span>
              <CrceDatePicker
                className="communications-crce-date"
                size="medium"
                placeholder="Start date"
                value={communicationsDateFrom}
                max={communicationsDateTo || undefined}
                onChange={(v) => handleCommunicationsDatePartChange('from', v)}
              />
            </div>
            <span className="communications-date-range-sep" aria-hidden>
              –
            </span>
            <div className="communications-crce-field">
              <span className="communications-crce-field-label" id="communications-to-label">
                To
              </span>
              <CrceDatePicker
                className="communications-crce-date"
                size="medium"
                placeholder="End date"
                value={communicationsDateTo}
                min={communicationsDateFrom || undefined}
                onChange={(v) => handleCommunicationsDatePartChange('to', v)}
              />
            </div>
            <CrceButton
              label="Reset"
              variant="tertiary"
              size="medium"
              onClick={handleCommunicationsDateRangeReset}
            />
          </div>
        </div>
      </div>

      <div className="communications-table-shell">
        <Table
          columns={columns}
          dataSource={[...dsrLog]}
          className="communications-dashboard-table"
          rowKey={(r) => r.communicationRegisterId ?? r.id ?? `${r.meetingTime}-${r.title}`}
          expandable={{
            columnWidth: 44,
            expandedRowRender: (record) => (
              <CommunicationExpand communicationRegisterId={record.communicationRegisterId} />
            ),
            expandIcon: ({ expanded, onExpand, record }) => (
              <button
                type="button"
                className="communications-expand-toggle"
                aria-expanded={expanded}
                aria-label={expanded ? 'Hide description' : 'Show description'}
                onClick={(e) => onExpand(record, e)}
              >
                {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
            ),
          }}
          scroll={{ x: 900, y: 'calc(100vh - 320px)' }}
          loading={getProjectTableLoadingConfig(isToolboxLoading)}
          locale={{ emptyText: <ProjectTabTableEmpty /> }}
          pagination={{
            className: 'heat-profile-pagination communications-table-pagination',
            current: communicationsPage,
            pageSize: communicationsPageSize,
            total: dsrLog.length,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} items`,
            onChange: (page, size) => {
              setCommunicationsPage(page);
              setCommunicationsPageSize(size);
            },
            onShowSizeChange: (_current, size) => {
              setCommunicationsPage(1);
              setCommunicationsPageSize(size);
            },
          }}
        />
      </div>
    </div>
  );
};

const mapStateToProps = ({ dsrReducer, toolBoxReducer }) => {
  const dsrLogs = get(dsrReducer, 'dsrLogsList', []);
  const isToolboxLoading = get(toolBoxReducer, 'isFetchtoolBoxDataLoading', []);
  const toolBoxData = get(toolBoxReducer, 'toolBoxHomeData', []);

  return {
    dsrLogs,
    isToolboxLoading,
    toolBoxData,
  };
};

const mapDispatchToProps = {
  onFetchDsrLogs: fetchDsrLogs,
  onFetchToolBox: fetchToolBoxHome,
};

export default connect(mapStateToProps, mapDispatchToProps)(ToolboxHome);
